import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { Link } from 'react-router-dom'

import http from '../../../../shared/http'
import { CONCEPT_NAME, TApi, TRoutes } from '../../../../shared/const'
import { bestandeneKapitel, leseKursstand, merkeKursstand } from '../../../../shared/courseProgress'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'

import PublicTopBar from '../../../PublicTopBar'
import PublicFooter from '../../../PublicFooter'

import { CHAPTERS } from '../../student/Concept/chapters'
import Quiz from '../../student/Concept/Quiz'
import kursStyles from '../../student/Concept/style.module.scss'

import CourseDialog from './CourseDialog'

import styles from './style.module.scss'

/**
 * Die Lernstrecke Skills4Retail — offen für alle, ohne Anmeldung.
 *
 * Dieselben zehn Kapitel und dieselben zehn Quizze wie im Bereich der
 * Schüler:innen: Text und Fragen stehen in `student/Concept/chapters.js` und
 * `quizzes.js`, gezeigt werden sie mit demselben Stylesheet. Ein zweiter
 * Kurstext daneben liefe schon beim nächsten Satz auseinander.
 *
 * **Was hier fehlt, fehlt mit Absicht:** die fünfzehn Konzeptfelder. Sie
 * gehören einer Junior Company, werden gemeinsam geschrieben und von einer
 * Lehrkraft bewertet — ohne Anmeldung gibt es nichts davon. Wer sie ausfüllen
 * will, braucht ein Konto; genau dorthin führt die Einladung.
 *
 * **Der Stand liegt im Browser** (`shared/courseProgress`). Nach drei
 * bestandenen Kapiteln lädt die Seite dazu ein, ihn ins Konto zu retten —
 * einmal, nicht bei jedem weiteren Kapitel.
 */

// Ab wann zur Anmeldung eingeladen wird. Drei Kapitel sind genug Arbeit, dass
// ihr Verlust weh taete, und wenig genug, dass die Einladung nicht nach einer
// Schranke aussieht.
const EINLADUNG_AB = 3

const GESEHEN = 'junior:s4r-kurs-begruessung'
const EINGELADEN = 'junior:s4r-kurs-einladung'

const merker = {
	lies: schluessel => {
		try {
			return window.localStorage.getItem(schluessel) === '1'
		} catch (error) {
			// Gesperrter Speicher heisst „noch nicht gesehen" — das Fenster geht
			// dann eben jedes Mal auf. Besser als gar keine Einladung.
			return false
		}
	},
	setze: schluessel => {
		try {
			window.localStorage.setItem(schluessel, '1')
		} catch (error) {
			// siehe oben
		}
	}
}

const VORGABE = { chapters: 10, questionsPerChapter: 5, quizPass: 4 }

export default function Skills4Retail() {
	const [config, setConfig] = useState(VORGABE)
	const [stand, setStand] = useState(leseKursstand)
	const [ansicht, setAnsicht] = useState(1)
	const [zeigeBegruessung, setZeigeBegruessung] = useState(false)
	const [zeigeEinladung, setZeigeEinladung] = useState(false)

	// Beim Kapitelwechsel an den Anfang springen — sonst steht man mitten im
	// naechsten Kapitel.
	const reiterRef = useRef(null)
	useScrollTopOnTabChange(ansicht, reiterRef)

	/*
	 * Die Huerde kommt vom Server, damit sie hier nicht anders steht als nach
	 * der Anmeldung. Antwortet er nicht, gilt die Vorgabe — ein Kurs, der wegen
	 * einer Zahl gar nicht erst aufgeht, waere die schlechtere Antwort.
	 */
	useEffect(() => {
		http.get(TApi.PUBLIC_COURSE, { disableToast: true })
			.then(({ data }) => setConfig({ ...VORGABE, ...data }))
			.catch(() => {})
	}, [])

	useEffect(() => {
		if (!merker.lies(GESEHEN)) setZeigeBegruessung(true)
	}, [])

	const standNach = useMemo(() => new Map(stand.map(eintrag => [eintrag.chapter, eintrag])), [stand])
	const bestanden = bestandeneKapitel(stand, config.quizPass)
	const kapitel = CHAPTERS.find(eintrag => eintrag.no === ansicht)
	// Hinter dem letzten Kapitel kommt keines mehr — dort bleibt es beim
	// Wiederholen, und wie es weitergeht, steht im Abschnitt „Und danach?".
	const naechstesKapitel = CHAPTERS.find(eintrag => eintrag.no === ansicht + 1)
	const vorigesKapitel = CHAPTERS.find(eintrag => eintrag.no === ansicht - 1)

	const meldeQuiz = ({ chapter, correct, total }) => {
		const neu = merkeKursstand({ chapter, correct, total })
		setStand(neu)

		if (bestandeneKapitel(neu, config.quizPass) >= EINLADUNG_AB && !merker.lies(EINGELADEN)) {
			merker.setze(EINGELADEN)
			setZeigeEinladung(true)
		}
	}

	const schliesseBegruessung = () => {
		merker.setze(GESEHEN)
		setZeigeBegruessung(false)
	}

	return (
		<div className={styles.root}>
			<PublicTopBar />

			<div className={styles.hero}>
				<div>
					<span className={styles.heroKicker}>Skills4Retail</span>
					<h1 className={styles.heroTitle}>{CONCEPT_NAME}</h1>
					<p className={styles.heroLead}>
						Zehn Kapitel darüber, wie ein Onlineshop wirklich funktioniert — mit einem Quiz nach jedem
						Kapitel. Kostenlos, ohne Konto, und du kannst jederzeit aufhören und weitermachen.
					</p>
				</div>

				<div className={styles.heroProgress}>
					<span className={styles.heroProgressNumber}>
						{bestanden}
						<i>/{config.chapters}</i>
					</span>
					<span className={styles.heroProgressLabel}>Kapitel bestanden</span>
					<span className={styles.heroProgressHint}>
						{bestanden === 0
							? `Bestanden ist ein Kapitel ab ${config.quizPass} von ${config.questionsPerChapter} richtigen Antworten.`
							: 'Dein Stand liegt in diesem Browser. Melde dich an, damit er nicht verloren geht.'}
					</span>
				</div>
			</div>

			<div className={styles.tabs} role="tablist" ref={reiterRef}>
				{CHAPTERS.map(eintrag => {
					const eigen = standNach.get(eintrag.no)
					const geschafft = eigen && eigen.correct >= config.quizPass

					return (
						<button
							key={eintrag.no}
							type="button"
							role="tab"
							aria-selected={ansicht === eintrag.no}
							title={eintrag.title}
							className={clsx(
								kursStyles.tab,
								ansicht === eintrag.no && kursStyles.tabActive,
								geschafft && kursStyles.tabDone
							)}
							onClick={() => setAnsicht(eintrag.no)}
						>
							{eintrag.no}
						</button>
					)
				})}
			</div>

			{/* Der Kapitelinhalt ist fest und steht in chapters.js. */}
			<div className={kursStyles.chapterBody} dangerouslySetInnerHTML={{ __html: kapitel.html }} />

			<div className={kursStyles.chapterBody}>
				<Quiz
					chapter={kapitel.no}
					quizPass={config.quizPass}
					stand={standNach.get(kapitel.no)}
					onSubmit={meldeQuiz}
					onWeiter={naechstesKapitel ? () => setAnsicht(naechstesKapitel.no) : undefined}
					weiterLabel={naechstesKapitel ? `Weiter zu Kapitel ${naechstesKapitel.no}` : undefined}
				/>
			</div>

			{/*
			 * Weiterblättern am Ende des Kapitels — dort, wo man ankommt, wenn
			 * man durch ist. Oben stehen die Reiter, aber wer unten fertig
			 * gelesen hat, sucht den nächsten Schritt nicht wieder ganz oben.
			 *
			 * Der Knopf nennt das Kapitel beim Namen: „Weiter: Wer ihr seid und
			 * für wen" sagt, was kommt — „Weiter" allein sagt nur, dass etwas
			 * kommt.
			 */}
			<nav className={styles.kapitelNav} aria-label="Kapitel wechseln">
				{vorigesKapitel ? (
					<button type="button" className={styles.navZurueck} onClick={() => setAnsicht(vorigesKapitel.no)}>
						<span className={styles.navRichtung}>← Kapitel {vorigesKapitel.no}</span>
						<span className={styles.navTitel}>{vorigesKapitel.title}</span>
					</button>
				) : (
					<span />
				)}

				{naechstesKapitel && (
					<button type="button" className={styles.navWeiter} onClick={() => setAnsicht(naechstesKapitel.no)}>
						<span className={styles.navRichtung}>Weiter · Kapitel {naechstesKapitel.no} →</span>
						<span className={styles.navTitel}>{naechstesKapitel.title}</span>
					</button>
				)}
			</nav>

			{/*
			 * Statt der fünfzehn Konzeptfelder steht hier, wozu es sie gibt und
			 * wie man an sie herankommt. Ohne diesen Hinweis wirkte der Kurs wie
			 * das Ganze — dabei ist er die halbe Sache.
			 *
			 * Ein Knopf, nicht zwei: Auf der Anmeldeseite steht der Weg zur
			 * Registrierung gleich daneben. Zwei Knöpfe hier zwangen zu einer
			 * Entscheidung, die man dort ohnehin noch einmal trifft.
			 */}
			<div className={clsx(kursStyles.chapterBody, styles.outro)}>
				<h3>Und dann wird es ernst</h3>
				<p>
					Was du hier lernst, bleibt nicht Theorie: Eure Junior Company baut daraus ihr eigenes
					E-Commerce-Konzept — eure Zielgruppe, euer Shop, eure Preise, eure Kanäle. Fünfzehn Felder, in denen
					ihr euer Geschäft aufschreibt und die eure Lehrkraft bewertet. Aus dem Kurs wird damit euer Plan.
				</p>
				{/*
				 * Hinweis und Knopf stehen zusammen in einem Kasten: Der Satz ist
				 * der Grund, sich anzumelden — daneben gehört der Weg dorthin und
				 * nicht drei Zeilen darunter.
				 */}
				<div className={styles.outroHinweis}>
					<p>
						<b>Dein Kursfortschritt geht dabei nicht verloren:</b> Meldest du dich mit deinem Konto an,
						werden die bestandenen Kapitel übernommen. Ohne Anmeldung liegen sie nur in diesem Browser — auf
						einem anderen Gerät oder nach dem Leeren des Verlaufs sind sie weg.
					</p>
					<Link to={TRoutes.SIGN_IN} className={styles.ctaHinweis}>
						Anmelden
					</Link>
				</div>
			</div>

			<PublicFooter />

			{zeigeBegruessung && (
				<CourseDialog
					kicker="Kostenlos · ohne Konto"
					title="Zehn Kapitel, zehn Quizze — probier dich aus"
					onClose={schliesseBegruessung}
					actions={
						<button type="button" className={styles.ctaPrimary} onClick={schliesseBegruessung}>
							Los geht’s
						</button>
					}
				>
					<p>
						Jedes Kapitel endet mit fünf Fragen. Bestanden hast du es ab {config.quizPass} richtigen
						Antworten — wiederholen darfst du so oft du willst, gezählt wird dein bestes Ergebnis.
					</p>
					<p>
						Dein Stand wird in diesem Browser gemerkt. Damit er dir erhalten bleibt, kannst du ihn später
						mit einem Konto verbinden.
					</p>
				</CourseDialog>
			)}

			{zeigeEinladung && (
				<CourseDialog
					kicker={`${bestanden} von ${config.chapters} Kapiteln geschafft`}
					title="Stark — sichere dir deinen Fortschritt"
					onClose={() => setZeigeEinladung(false)}
					actions={
						<>
							<Link to={TRoutes.SIGN_IN} className={styles.ctaPrimary}>
								Anmelden
							</Link>
							<button type="button" className={styles.ctaGhost} onClick={() => setZeigeEinladung(false)}>
								Später
							</button>
						</>
					}
				>
					<p>
						Dein Stand liegt bisher nur in diesem Browser. Wer den Verlauf löscht oder das Gerät wechselt,
						beginnt von vorn.
					</p>
					<p>
						Meldest du dich an, wird dein Ergebnis übernommen — alle {bestanden} bestandenen Kapitel zählen
						dort weiter. Der Kurs geht hier auch ohne Konto zu Ende; du kannst dich später immer noch
						anmelden.
					</p>
				</CourseDialog>
			)}
		</div>
	)
}
